import { memo } from 'react'

import { GraphicEq, Videocam } from '@mui/icons-material'
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

/** One camera or global audio of an event's config, reduced to what a table cell can show. */
export type SummaryItem = {
  name: string
  enabled: boolean
  /** Shown after the name in the tooltip — e.g. the camera a stream is named for. */
  secondary?: string
  /** The name is still being resolved; render a placeholder rather than a stale label. */
  pending?: boolean
}

const EmptyMark = () => (
  <Typography variant="inherit" sx={{ color: 'text.disabled' }}>
    —
  </Typography>
)

export type MediaSummaryProps = {
  kind: 'video' | 'audio'
  items: SummaryItem[]
}

/**
 * An event has any number of cameras and global audios — usually a few, sometimes none.
 * The cell names the first one and counts the rest; the tooltip carries the full list, so
 * the column stays one line wide whether the event has one angle or twelve.
 */
const MediaSummaryRaw = ({ kind, items }: MediaSummaryProps) => {
  const { t } = useTranslation(['common', 'domain'])

  if (!items.length) {
    return <EmptyMark />
  }

  const [first, ...rest] = items
  const Icon = kind === 'video' ? Videocam : GraphicEq
  const label = (item: SummaryItem) => (item.pending ? '…' : item.name || t('noNameProvided'))

  return (
    <Tooltip
      placement="top"
      title={
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {items.map((item, index) => (
            <li key={index}>
              {label(item)}
              {!!item.secondary && ` — ${item.secondary}`}
              {!item.enabled && ` (${t('domain:Workspace.Event.ConfigEditor.disabled')})`}
            </li>
          ))}
        </Box>
      }
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Icon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
        {first.pending ? (
          <Skeleton variant="text" sx={{ flex: 1, minWidth: 40 }} />
        ) : (
          <Typography variant="inherit" noWrap sx={[!first.enabled && { color: 'text.disabled' }]}>
            {label(first)}
          </Typography>
        )}
        {!!rest.length && (
          <Typography variant="inherit" sx={{ color: 'text.secondary', flexShrink: 0 }}>
            {`+${rest.length}`}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}

export const MediaSummary = memo(MediaSummaryRaw)

/** How many tags fit next to each other before the rest collapse into a counter. */
const VISIBLE_TAGS = 2

export type TagSummaryProps = {
  tags: string[]
}

const TagSummaryRaw = ({ tags }: TagSummaryProps) => {
  if (!tags.length) {
    return <EmptyMark />
  }

  const visible = tags.slice(0, VISIBLE_TAGS)
  const hidden = tags.length - visible.length

  return (
    <Tooltip placement="top" title={tags.join(', ')}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        {visible.map((tag) => (
          <Chip key={tag} size="small" label={tag} sx={{ maxWidth: 120 }} />
        ))}
        {hidden > 0 && (
          <Typography variant="inherit" sx={{ color: 'text.secondary', flexShrink: 0 }}>
            {`+${hidden}`}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}

export const TagSummary = memo(TagSummaryRaw)
